/**
 * The move gadget of the 3D view. At the selected element's center stands an
 * arrow along each axis (drag one to move along that axis alone) and a
 * rounded tile in each plane (drag one to move within that plane). Every
 * piece is a thin extrusion: white on the face toward the camera, dark on
 * its sides and back, outlined in ink, and sized in screen pixels so it reads
 * the same at any zoom. Each arrow turns about its own axis to face the
 * camera as far as it can and points along its axis toward the camera, so
 * every head comes out of the screen whatever the view; each tile sits in the
 * quadrant of its plane that faces the camera and shows it its white face. Built fresh for every frame from the camera and
 * drawn over everything, so a wall never hides it. Under touch it stands 1.85
 * times the size, and a tap takes the handle whose center is nearest, within
 * a touch target's reach, wherever no outline is under it.
 *
 * Handles: `x` and `y` are the plan axes, `z` is height. In world terms plan
 * x is world x, plan y is world z, and height is world y.
 */
import { type InputKind, TOUCH_TARGET_REACH } from '../editor-model';
import { add, cross, dot3d, length, normalize, rayPlaneDistance, scale, sub, triangulatePolygon, type Vec3 } from '../math3d';
import { ALWAYS, MeshBuilder, type RGB } from './mesh3d';
import { evenOddContains, type PickRay } from './pick3d';

export type GadgetAxis = 'x' | 'y' | 'z';
export type GadgetHandle = GadgetAxis | 'xy' | 'yz' | 'zx';

/** Where the gadget stands and whether it offers the height handles; what it moves is the host's selection. */
export interface SceneGadget {
  center: Vec3;
  /** The selection has a height to move through (an object, a counter, or a room's floor elevation); floors and walls move in plan only. */
  vertical: boolean;
}

/** A drag's progress, in plan feet (`x`, `y`) and height (`z`), from where it began. */
export interface GadgetDelta {
  x: number;
  y: number;
  z: number;
}

const AXES: Record<GadgetAxis, Vec3> = { x: [1, 0, 0], y: [0, 0, 1], z: [0, 1, 0] };
const PLANES: Record<'xy' | 'yz' | 'zx', [GadgetAxis, GadgetAxis]> = { xy: ['x', 'y'], yz: ['y', 'z'], zx: ['z', 'x'] };

/** The gadget's sizes, in screen pixels. */
interface GadgetSizes {
  arrowStart: number;
  shaftLength: number;
  shaftHalf: number;
  headLength: number;
  headHalf: number;
  tileOffset: number;
  tileHalf: number;
  tileRound: number;
}

/** A mouse's sizes, and 1.85 times them under touch, so neighboring handles' centers stand about a touch target apart. */
const GADGET_SIZES: Record<InputKind, GadgetSizes> = {
  pointer: { arrowStart: 22, shaftLength: 24, shaftHalf: 3.5, headLength: 14, headHalf: 9, tileOffset: 30, tileHalf: 12, tileRound: 3.5 },
  touch: { arrowStart: 41, shaftLength: 44, shaftHalf: 6.5, headLength: 26, headHalf: 17, tileOffset: 56, tileHalf: 22, tileRound: 6.5 },
};
const THICKNESS = 3;
/** Click forgiveness around a handle's outline (px). */
const HIT_PAD = 4;

const WHITE: RGB = [1, 1, 1];
const SIDE: RGB = [0.3, 0.3, 0.32];
const ACTIVE: RGB = [0.99, 1, 0.53];

/** A handle's shape in its own (along, across) frame, in px: its outline and its visual center. */
interface HandleShape {
  outline: [number, number][];
  center: [number, number];
}

/** The arrow: a shaft ending in a head, centered halfway along its length. */
function arrowShape({ arrowStart, shaftLength, shaftHalf, headLength, headHalf }: GadgetSizes): HandleShape {
  const headStart = arrowStart + shaftLength;
  return {
    outline: [
      [arrowStart, -shaftHalf],
      [headStart, -shaftHalf],
      [headStart, -headHalf],
      [headStart + headLength, 0],
      [headStart, headHalf],
      [headStart, shaftHalf],
      [arrowStart, shaftHalf],
    ],
    center: [arrowStart + (shaftLength + headLength) / 2, 0],
  };
}

/** The tile: a rounded square out along both of a plane's axes, its corners cut by short arcs. */
function tileShape({ tileOffset, tileHalf, tileRound }: GadgetSizes): HandleShape {
  const outline: [number, number][] = [];
  const corners: [number, number][] = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  corners.forEach(([sx, sy], corner) => {
    const cx = tileOffset + sx * (tileHalf - tileRound), cy = tileOffset + sy * (tileHalf - tileRound);
    for (let step = 0; step <= 3; step++) {
      const angle = (corner * 90 + step * 30) * (Math.PI / 180);
      outline.push([cx + tileRound * Math.cos(angle), cy + tileRound * Math.sin(angle)]);
    }
  });
  return { outline, center: [tileOffset, tileOffset] };
}

/** Each input's arrow and tile, built once. */
const HANDLE_SHAPES: Record<InputKind, { arrow: HandleShape; tile: HandleShape }> = {
  pointer: { arrow: arrowShape(GADGET_SIZES.pointer), tile: tileShape(GADGET_SIZES.pointer) },
  touch: { arrow: arrowShape(GADGET_SIZES.touch), tile: tileShape(GADGET_SIZES.touch) },
};

/** One handle's hit shape: its outline and visual center (px) on the plane through `origin` spanned by `u` and `v` (feet per px: `feetPerPixel`). */
export interface GadgetHit {
  handle: GadgetHandle;
  origin: Vec3;
  u: Vec3;
  v: Vec3;
  outline: readonly [number, number][];
  center: readonly [number, number];
}

export interface GadgetLayout {
  mesh: MeshBuilder;
  hits: GadgetHit[];
  feetPerPixel: number;
  /** How far (px) from a handle's center a pick still takes it where no outline is under it: a touch target's reach, or none for a mouse. */
  centerReach: number;
}

/**
 * An extruded flat shape: `outline` (px) in the (u, v) frame at `origin`,
 * `n` its face normal toward the camera. The top face takes `face`, the
 * sides and back the dark side color, and the top face is outlined in ink.
 */
function extrude(mb: MeshBuilder, origin: Vec3, u: Vec3, v: Vec3, n: Vec3, outline: readonly [number, number][], feetPerPixel: number, face: RGB): void {
  const at = ([s, t]: readonly [number, number], lift: number): Vec3 =>
    add(add(origin, add(scale(u, s * feetPerPixel), scale(v, t * feetPerPixel))), scale(n, lift));
  const half = (THICKNESS * feetPerPixel) / 2;
  const triangles = triangulatePolygon([outline.map(([x, y]) => ({ x, y }))]);
  const top = outline.map(p => at(p, half)), bottom = outline.map(p => at(p, -half));
  // Back, sides, then the face toward the camera; the overlay pass depth-tests the gadget against itself.
  for (const triangle of triangles) mb.fan(triangle.map(p => at([p.x, p.y], -half)), scale(n, -1), SIDE);
  for (let i = 0; i < outline.length; i++) {
    const j = (i + 1) % outline.length;
    mb.quad(top[i], top[j], bottom[j], bottom[i], [0, 0, 0], SIDE);
  }
  for (const triangle of triangles) mb.fan(triangle.map(p => at([p.x, p.y], half)), n, face);
  mb.polyline(top, true);
}

/** The gadget for one frame: its mesh and hit shapes, built for the camera at `eye`, sized for `inputKind`. */
export function buildGadget(
  gadget: SceneGadget,
  eye: Vec3,
  feetPerPixel: number,
  active: GadgetHandle | null,
  inputKind: InputKind = 'pointer',
): GadgetLayout {
  const { arrow, tile } = HANDLE_SHAPES[inputKind];
  const mb = new MeshBuilder();
  mb.begin(ALWAYS);
  const hits: GadgetHit[] = [];
  const toEye = sub(eye, gadget.center);
  const face = (handle: GadgetHandle): RGB => handle === active ? ACTIVE : WHITE;

  const axes: GadgetAxis[] = gadget.vertical ? ['x', 'y', 'z'] : ['x', 'y'];
  for (const handle of axes) {
    // The arrow points along its axis toward the camera, so its head comes out of the screen from any view.
    const axis = scale(AXES[handle], dot3d(toEye, AXES[handle]) >= 0 ? 1 : -1);
    // It turns about that axis toward the camera: its face normal is the eye direction with the axis component removed.
    let n = sub(toEye, scale(axis, dot3d(toEye, axis)));
    if (length(n) < 1e-6) n = handle === 'z' ? [0, 0, 1] : [0, 1, 0]; // looking straight down the axis: any face will do
    n = normalize(n);
    const across = normalize(cross(n, axis));
    extrude(mb, gadget.center, axis, across, n, arrow.outline, feetPerPixel, face(handle));
    hits.push({ handle, origin: gadget.center, u: axis, v: across, ...arrow });
  }

  const planes: ('xy' | 'yz' | 'zx')[] = gadget.vertical ? ['xy', 'yz', 'zx'] : ['xy'];
  for (const handle of planes) {
    // The tile sits in the quadrant of its plane that faces the camera: out along both axes toward the eye.
    const [a, b] = PLANES[handle];
    const u = scale(AXES[a], dot3d(toEye, AXES[a]) >= 0 ? 1 : -1);
    const v = scale(AXES[b], dot3d(toEye, AXES[b]) >= 0 ? 1 : -1);
    let n = cross(u, v);
    if (dot3d(n, toEye) < 0) n = scale(n, -1); // the white face toward the camera
    extrude(mb, gadget.center, u, v, n, tile.outline, feetPerPixel, face(handle));
    hits.push({ handle, origin: gadget.center, u, v, ...tile });
  }
  mb.end();
  return { mesh: mb, hits, feetPerPixel, centerReach: inputKind === 'touch' ? TOUCH_TARGET_REACH : 0 };
}

/** Where a ray meets a hit shape's plane, as (s, t) px in its frame and the ray distance; null when the ray runs along the plane or the plane is behind. */
function planeHit(hit: GadgetHit, ray: PickRay, feetPerPixel: number): { s: number; t: number; distance: number } | null {
  const distance = rayPlaneDistance(ray.origin, ray.dir, hit.origin, cross(hit.u, hit.v));
  if (distance === null) return null;
  const point = sub(add(ray.origin, scale(ray.dir, distance)), hit.origin);
  return { s: dot3d(point, hit.u) / feetPerPixel, t: dot3d(point, hit.v) / feetPerPixel, distance };
}

/** Whether (s, t) lies in an outline, or within the click pad of one of its edges. */
function insideOutline(outline: readonly [number, number][], s: number, t: number): boolean {
  if (evenOddContains([outline], s, t)) return true;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const [ax, ay] = outline[i], [bx, by] = outline[j];
    const dx = bx - ax, dy = by - ay;
    const along = Math.max(0, Math.min(1, ((s - ax) * dx + (t - ay) * dy) / (dx * dx + dy * dy || 1)));
    if (Math.hypot(s - (ax + along * dx), t - (ay + along * dy)) <= HIT_PAD) return true;
  }
  return false;
}

/** The handle whose outline the ray passes through, nearest the eye first, or null. */
function outlineUnder(layout: GadgetLayout, ray: PickRay): GadgetHandle | null {
  let best: GadgetHandle | null = null, bestDistance = Infinity;
  for (const hit of layout.hits) {
    const at = planeHit(hit, ray, layout.feetPerPixel);
    if (at && at.distance < bestDistance && insideOutline(hit.outline, at.s, at.t)) {
      best = hit.handle;
      bestDistance = at.distance;
    }
  }
  return best;
}

/** How far (px on screen) the ray passes from a handle's visual center, or Infinity when the center is behind the eye. */
function centerMiss(hit: GadgetHit, ray: PickRay, feetPerPixel: number): number {
  const [s, t] = hit.center;
  const toCenter = sub(add(hit.origin, add(scale(hit.u, s * feetPerPixel), scale(hit.v, t * feetPerPixel))), ray.origin);
  const along = dot3d(toCenter, ray.dir);
  return along <= 0 ? Infinity : length(sub(toCenter, scale(ray.dir, along))) / feetPerPixel;
}

/** The handle whose visual center the ray passes nearest, within the layout's center reach, or null. */
function nearestCenter(layout: GadgetLayout, ray: PickRay): GadgetHandle | null {
  let best: GadgetHandle | null = null, bestMiss = layout.centerReach;
  for (const hit of layout.hits) {
    const miss = centerMiss(hit, ray, layout.feetPerPixel);
    if (miss <= bestMiss) {
      best = hit.handle;
      bestMiss = miss;
    }
  }
  return best;
}

/** The handle under a ray: the nearest whose outline it passes through, else (on touch, which has a reach) the one whose center it passes nearest within reach, or null. */
export function pickGadget(layout: GadgetLayout, ray: PickRay): GadgetHandle | null {
  return outlineUnder(layout, ray) ?? (layout.centerReach > 0 ? nearestCenter(layout, ray) : null);
}

/**
 * A drag in progress. An axis drag follows the point of the axis line nearest
 * the pointer's ray; a plane drag follows the pointer's ray on the plane. Both
 * keep the point first grabbed under the pointer. `last` is what the drag
 * reported when the pointer's ray could not be read (looking along the axis
 * or the plane), so the element holds still rather than jumping.
 */
export type GadgetDrag =
  | { handle: GadgetAxis; center: Vec3; axis: Vec3; startAlong: number; last: GadgetDelta }
  | { handle: 'xy' | 'yz' | 'zx'; origin: Vec3; normal: Vec3; start: Vec3; last: GadgetDelta };

const isAxisDrag = (drag: GadgetDrag): drag is Extract<GadgetDrag, { axis: Vec3 }> => 'axis' in drag;

/** World-space deltas as the plan sees them: plan x is world x, plan y is world z, height is world y. */
const toDelta = ([x, y, z]: Vec3): GadgetDelta => ({ x, y: z, z: y });

/** Rays closer to parallel with an axis than this cannot place a point on it. */
const ALONG_EPSILON = 1e-3;

/** How far along the axis line through `center` the point nearest the ray lies; null when the ray runs along the axis. */
function alongAxis(center: Vec3, axis: Vec3, ray: PickRay): number | null {
  const w = sub(center, ray.origin);
  const b = dot3d(axis, ray.dir), d = dot3d(axis, w), e = dot3d(ray.dir, w);
  const denominator = 1 - b * b; // both unit vectors
  if (denominator < ALONG_EPSILON) return null;
  return (b * e - d) / denominator;
}

function rayOnPlane(ray: PickRay, origin: Vec3, normal: Vec3): Vec3 | null {
  const distance = rayPlaneDistance(ray.origin, ray.dir, origin, normal);
  return distance === null ? null : add(ray.origin, scale(ray.dir, distance));
}

/** Begin a drag on `handle` with the pointer's ray; null when the ray cannot read the handle's axis or plane. */
export function beginGadgetDrag(handle: GadgetHandle, center: Vec3, ray: PickRay): GadgetDrag | null {
  const last = { x: 0, y: 0, z: 0 };
  if (handle === 'x' || handle === 'y' || handle === 'z') {
    const axis = AXES[handle];
    const startAlong = alongAxis(center, axis, ray);
    return startAlong === null ? null : { handle, center, axis, startAlong, last };
  }
  const [a, b] = PLANES[handle];
  const normal = cross(AXES[a], AXES[b]);
  const start = rayOnPlane(ray, center, normal);
  return start ? { handle, origin: center, normal, start, last } : null;
}

/** How far the drag has come, in plan feet and height, for the pointer's ray now. */
export function gadgetDragDelta(drag: GadgetDrag, ray: PickRay): GadgetDelta {
  if (isAxisDrag(drag)) {
    const along = alongAxis(drag.center, drag.axis, ray);
    if (along !== null) drag.last = toDelta(scale(drag.axis, along - drag.startAlong));
    return drag.last;
  }
  const at = rayOnPlane(ray, drag.origin, drag.normal);
  if (at) drag.last = toDelta(sub(at, drag.start));
  return drag.last;
}
